import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';
import inventoryItemService from '../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import InventoryItemSearchBox from './InventoryItemSearchBox';
import InventoryRecipeEditor from './InventoryRecipeEditor';

/**
 * MENU-EXEC.4 — item-level "how is this sold?" builder.
 *
 * A MenuItem is placement; the sellable/executable thing is an InventoryItem.
 * This panel lets the operator choose how the placement is backed, and reports
 * the chosen InventoryItem (or null) up to the parent form, which owns the
 * MenuItem.inventoryItemId write. It NEVER creates/updates the MenuItem itself.
 *
 * Modes:
 *   LINK_EXISTING     — pick an existing InventoryItem (direct-stock or recipe).
 *   CREATE_RECIPE_ITEM— create a RECIPE_CONSUMPTION InventoryItem + its recipe.
 *   MANUAL_DISPLAY    — no inventory link; parent uses the manual menu price.
 *
 * Uses existing endpoints only (inventoryItemService.createItem,
 * inventoryItemRecipeService via InventoryRecipeEditor). No new backend API.
 */

export type FulfillmentMode = 'LINK_EXISTING' | 'CREATE_RECIPE_ITEM' | 'MANUAL_DISPLAY';

interface ItemFulfillmentPanelProps {
  venueId: string;
  mode: FulfillmentMode;
  onModeChange: (mode: FulfillmentMode) => void;
  linkedItem: InventoryItemWithBalance | null;
  /** Report the chosen InventoryItem (or null) to the parent form. */
  onLinkedItemChange: (item: InventoryItemWithBalance | null) => void;
  defaultCurrency: string;
  /** Pre-fill for a created recipe item's name (e.g. the menu item name). */
  suggestedName?: string;
}

function extractCreateError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errObj?.['code'];
    if (status === 409 || code === 'INVENTORY_ITEM_DUPLICATE') {
      return "An inventory item with this name already exists — use 'Link existing item' instead.";
    }
    if (status === 403) return 'You do not have permission to create inventory items.';
    const msg = errObj?.['message'] ?? data?.['message'];
    if (typeof msg === 'string' && msg) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not create the inventory item. Please try again.';
}

function LinkedItemSummary({
  venueId,
  item,
  defaultCurrency,
}: {
  venueId: string;
  item: InventoryItemWithBalance;
  defaultCurrency: string;
}) {
  const isRecipe = item.executionBehavior === 'RECIPE_CONSUMPTION';
  const price = item.salePrice != null
    ? `${(item.salePrice / 100).toFixed(2)} ${item.salePriceCurrency ?? defaultCurrency}`
    : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 160 }}>
          {item.displayName}
        </Typography>
        <Chip
          size="small"
          label={isRecipe ? 'Made from a recipe' : 'Sold from stock'}
          color={isRecipe ? 'secondary' : 'info'}
          variant="outlined"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {price ? `Selling price: ${price}` : 'No selling price set on the inventory item.'}
        {' · '}
        Price and recipe are managed on the inventory item.
      </Typography>
      <Link
        href={`/admin/venues/${venueId}/inventory/${item._id}`}
        target="_blank"
        rel="noopener"
        variant="caption"
        sx={{ display: 'inline-block', mt: 0.5 }}
      >
        Edit inventory item ↗
      </Link>
    </Paper>
  );
}

export default function ItemFulfillmentPanel({
  venueId,
  mode,
  onModeChange,
  linkedItem,
  onLinkedItemChange,
  defaultCurrency,
  suggestedName,
}: ItemFulfillmentPanelProps) {
  // Create-recipe-item local state.
  const [createName, setCreateName] = useState('');
  const [createPrice, setCreatePrice] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [recipeSaved, setRecipeSaved] = useState(false);

  useEffect(() => {
    if (mode === 'CREATE_RECIPE_ITEM' && !linkedItem) {
      setCreateName(suggestedName ?? '');
    }
  }, [mode, linkedItem, suggestedName]);

  const handleModeChange = (next: FulfillmentMode | null) => {
    if (!next || next === mode) return;
    setCreateError(null);
    setRecipeSaved(false);
    // Switching to manual clears the inventory link. Switching between
    // link/create keeps any already-chosen item so the user can review it.
    if (next === 'MANUAL_DISPLAY') {
      onLinkedItemChange(null);
    }
    onModeChange(next);
  };

  const handleCreateRecipeItem = async () => {
    const displayName = createName.trim();
    if (!displayName) { setCreateError('Enter a name for the recipe item.'); return; }
    const priceMajor = createPrice ? parseFloat(createPrice) : NaN;
    if (createPrice && (isNaN(priceMajor) || priceMajor < 0)) {
      setCreateError('Selling price must be a non-negative number.');
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const created = await inventoryItemService.createItem(venueId, {
        // RECIPE-TYPE.R7 — recipe-execution items are now stored as type RECIPE
        // (was PURCHASED). executionBehavior is unchanged and still drives depletion.
        type: 'RECIPE',
        displayName,
        executionBehavior: 'RECIPE_CONSUMPTION',
        // salePrice is stored in MINOR units; the field is human major units.
        ...(createPrice ? { salePrice: Math.round(priceMajor * 100), salePriceCurrency: defaultCurrency } : {}),
      });
      // Report the link up immediately — even if the recipe step later fails,
      // the created item stays linked and editable (no auto-delete).
      onLinkedItemChange(created as unknown as InventoryItemWithBalance);
    } catch (err) {
      setCreateError(extractCreateError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => handleModeChange(v as FulfillmentMode | null)}
        sx={{ mb: 1.5, flexWrap: 'wrap' }}
      >
        <ToggleButton value="LINK_EXISTING" sx={{ textTransform: 'none' }}>Link existing item</ToggleButton>
        <ToggleButton value="CREATE_RECIPE_ITEM" sx={{ textTransform: 'none' }}>Create recipe item</ToggleButton>
        <ToggleButton value="MANUAL_DISPLAY" sx={{ textTransform: 'none' }}>Manual (display only)</ToggleButton>
      </ToggleButtonGroup>

      {/* ── LINK EXISTING ──────────────────────────────────────────────── */}
      {mode === 'LINK_EXISTING' && (
        <Box>
          <InventoryItemSearchBox
            venueId={venueId}
            value={linkedItem}
            onChange={onLinkedItemChange}
            showExecutionBehavior
            label="Link an existing inventory item"
            helperText="Search inventory. POS sells this item and deducts its stock or runs its recipe."
          />
          {linkedItem && (
            <LinkedItemSummary venueId={venueId} item={linkedItem} defaultCurrency={defaultCurrency} />
          )}
        </Box>
      )}

      {/* ── CREATE RECIPE ITEM ─────────────────────────────────────────── */}
      {mode === 'CREATE_RECIPE_ITEM' && (
        <Box>
          {!linkedItem ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Creates a recipe-backed inventory item (executes from a recipe at POS). Set its name and
                selling price, then add ingredients.
              </Typography>
              {createError && (
                <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>{createError}</Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <TextField
                  size="small"
                  label="Recipe item name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  disabled={creating}
                  sx={{ flex: 2, minWidth: 220 }}
                />
                <TextField
                  size="small"
                  label="Selling price"
                  value={createPrice}
                  onChange={(e) => setCreatePrice(e.target.value)}
                  type="number"
                  disabled={creating}
                  sx={{ width: 140 }}
                  helperText={defaultCurrency ? `In ${defaultCurrency}` : undefined}
                  slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                />
                <Chip size="small" label="Made from a recipe" color="secondary" variant="outlined" />
              </Stack>
              <Button
                variant="contained"
                size="small"
                onClick={handleCreateRecipeItem}
                disabled={creating}
                sx={{ textTransform: 'none', mt: 1.5 }}
              >
                {creating ? 'Creating…' : 'Create recipe item'}
              </Button>
            </>
          ) : (
            <>
              <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
                Created <strong>{linkedItem.displayName}</strong> as a recipe item. Add its ingredients below;
                this menu item links to it when you save.
              </Alert>
              <InventoryRecipeEditor
                venueId={venueId}
                itemId={linkedItem._id}
                initialComponents={null}
                onSaved={() => setRecipeSaved(true)}
                onCancel={() => { /* keep the linked item; recipe can be edited later */ }}
              />
              {recipeSaved && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                  Recipe saved.
                </Typography>
              )}
              <Button
                size="small"
                color="inherit"
                onClick={() => { onLinkedItemChange(null); setRecipeSaved(false); }}
                sx={{ textTransform: 'none', mt: 1 }}
              >
                Use a different item
              </Button>
            </>
          )}
        </Box>
      )}

      {/* ── MANUAL DISPLAY-ONLY ────────────────────────────────────────── */}
      {mode === 'MANUAL_DISPLAY' && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1 }}>
          Display only — this menu item is not connected to stock or POS execution. It uses the manual
          menu price below and will not deduct inventory when sold.
        </Alert>
      )}
    </Box>
  );
}
